import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { createProposal } from "@/lib/api";
import CityAutocomplete from "@/components/CityAutocomplete";

const CATEGORIES = [
  { value: "manifestation", label: "Manifestation", color: "#2563EB" },
  { value: "blocage_routier", label: "Blocage", color: "#DC2626" },
  { value: "perturbation_transport", label: "Transport", color: "#EA580C" },
];

export default function ProposeDialog({ open, onClose, defaultType }) {
  const [form, setForm] = useState({ types: [], city: "", date: "", time: "", lieu: "", description: "", source_url: "", website: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const preset = CATEGORIES.some((c) => c.value === defaultType) ? [defaultType] : [];
      setForm((f) => ({ ...f, types: preset }));
    }
  }, [open, defaultType]);

  const toggleCat = (value) => {
    setForm((f) => ({ ...f, types: f.types.includes(value) ? f.types.filter((t) => t !== value) : [...f.types, value] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.types.length) return toast.error("Choisissez au moins une catégorie");
    if (!form.city || !form.date || !form.time || !form.lieu) return toast.error("Ville, date, heure et lieu sont requis");
    setLoading(true);
    try {
      const r = await createProposal(form);
      if (r.status === "published") toast.success("Rendez-vous publié : seuil de confirmations atteint");
      else toast.success("Merci. Visible après une 2ᵉ proposition d'une autre personne.");
      setForm({ types: [], city: "", date: "", time: "", lieu: "", description: "", source_url: "", website: "" });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible");
    } finally {
      setLoading(false);
    }
  };

  const inp = "w-full h-11 px-3 rounded-lg bg-[#0B0F17] border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-sky-500";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-[950] bg-black/60" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div data-testid="propose-dialog"
            className="fixed z-[951] inset-x-0 bottom-0 sm:inset-0 sm:m-auto sm:h-fit sm:max-w-md bg-[#0B0F17] border-t sm:border border-slate-700 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 300 }}>
            <div className="sticky top-0 bg-[#0B0F17]/95 backdrop-blur px-5 py-3 border-b border-slate-800 flex items-center justify-between">
              <h3 className="font-bold text-slate-50">Proposer un rendez-vous</h3>
              <button data-testid="propose-close" onClick={onClose} className="h-9 w-9 flex items-center justify-center rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="px-5 py-4 space-y-3">
              {/* 1. Catégorie(s) — une ou plusieurs (mêmes repères couleur qu'en haut de la carte) */}
              <div>
                <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Catégorie(s) — une ou plusieurs</label>
                <div className="grid grid-cols-3 gap-2">
                  {CATEGORIES.map((c) => {
                    const on = form.types.includes(c.value);
                    return (
                      <button key={c.value} type="button" data-testid={`propose-cat-${c.value}`} aria-pressed={on}
                        onClick={() => toggleCat(c.value)}
                        className={`relative h-14 rounded-xl border flex flex-col items-center justify-center gap-1 text-[12px] font-semibold transition ${on ? "border-sky-500 bg-sky-950/40 text-slate-50" : "border-slate-700 bg-[#151C28] text-slate-300"}`}>
                        {on && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-sky-500 flex items-center justify-center"><Check size={11} className="text-white" /></span>}
                        <span className="h-3 w-3 rounded-full" style={{ background: c.color }} />
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <p className="text-xs text-slate-400 leading-snug">Classé « Annonce ». Un rendez-vous n'est visible qu'après avoir été proposé par au moins deux personnes (2 adresses différentes).</p>

              {/* 2. Détails */}
              <CityAutocomplete testid="propose-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })}
                onSelect={(item) => setForm({ ...form, city: item.name })}
                placeholder="Ville *" inputClassName={inp + " pl-8"} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="propose-date" type="date" className={inp} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                <input data-testid="propose-time" type="time" className={inp} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
              </div>
              <input data-testid="propose-lieu" className={inp} placeholder="Lieu précis (adresse, place) *" value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} />
              <textarea data-testid="propose-desc" className={inp + " h-20 py-2"} placeholder="Description — ce qui se passe" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <input data-testid="propose-source" className={inp} placeholder="Lien source (facultatif)" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} />
              {/* honeypot anti-spam : caché */}
              <input name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} />
              <button data-testid="propose-submit" type="submit" disabled={loading}
                className="w-full h-11 rounded-lg bg-sky-600 hover:bg-sky-500 font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Envoyer la proposition
              </button>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
